'use client';

import { Controller, ValidationRule } from 'react-hook-form';
import { TelephoneField } from '@/shared/ui/fields/telephone-field';
import { FormFieldMessage } from './form-field-message';
import { FormFieldProps } from './types';
import { getFormFieldState } from './utils/getFormFieldState';
import { mergeRefs } from './utils/mergeRefs';

type Props = FormFieldProps<typeof TelephoneField>;

const requiredTelephoneValidation = (
    text: string,
    originalValue: string | boolean | ValidationRule<boolean>,
) => {
    if (!text?.split(' ')[1]?.length) {
        switch (typeof originalValue) {
            case 'string': {
                return originalValue;
            }
            case 'boolean': {
                return false;
            }
            default: {
                return (originalValue as { message?: string }).message;
            }
        }
    }
    return true;
};

export const FormTelephoneField = ({
    name,
    control,
    defaultValue,
    disabled,
    rules,
    shouldUnregister,
    messageWrapperProps,
    isErrorVisible = true,
    isSuccessVisible,
    ref,
    ...props
}: Props) => {
    return (
        <Controller
            {...{
                name,
                control,
                defaultValue,
                disabled,
                rules: {
                    ...rules,
                    validate: {
                        ...((typeof rules?.required === 'object' && rules.required.value === true) ||
                        typeof rules?.required === 'string' ||
                        typeof rules?.required === 'boolean'
                            ? {
                                  __required: (value: string) =>
                                      requiredTelephoneValidation(value, rules.required!),
                              }
                            : {}),
                        ...rules?.validate,
                    },
                    required: undefined,
                },
                shouldUnregister,
            }}
            render={({
                field: { ref: fieldRef, ...field },
                fieldState: { error, isTouched, invalid, isDirty },
            }) => {
                const state = getFormFieldState({
                    invalid,
                    isDirty,
                    isTouched,
                    isErrorVisible,
                    isSuccessVisible,
                });
                return (
                    <div>
                        <TelephoneField
                            ref={mergeRefs([fieldRef, ref])}
                            state={state}
                            required={rules?.required !== undefined}
                            {...field}
                            {...props}
                        />
                        <FormFieldMessage
                            state={state}
                            error={error}
                            messageWrapperProps={messageWrapperProps}
                        />
                    </div>
                );
            }}
        />
    );
};
